import { useEffect, useRef, useState, useMemo } from 'react';

import { useIntersectionObserver } from './useIntersectionObserver';

export interface UseAnimatedCounterOptions {
  /** How many units per second the counter should grow */
  growthRatio?: number;
  /** Determines whether the counter should continuously grow or animate to a static target */
  targetMode?: 'growth' | 'staticTarget';
  /** Target number to animate to when targetMode = 'staticTarget' */
  targetNumber?: number;
  /** Starting number */
  from?: number;
  /** Seconds to wait AFTER each animation completes before starting the next one */
  delay?: number;
  /** Duration in seconds of the animation itself */
  duration?: number;
  /** Mode that decides how the start value is calculated */
  mode?: 'static' | 'startDate' | 'currentMonth';
  /** ISO date string representing the moment the counter started (required if mode = 'startDate') */
  startDate?: string;
  /** Callbacks */
  onStart?: () => void;
  onEnd?: () => void;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export function useAnimatedCounter({
  growthRatio,
  targetMode = 'growth',
  targetNumber,
  from = 0,
  delay = 3,
  duration = 2,
  mode = 'static',
  startDate,
  onStart,
  onEnd,
}: UseAnimatedCounterOptions) {
  const animationFrameRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
    freezeOnceVisible: true,
  });

  const computedFrom = useMemo(() => {
    if (!growthRatio) {
      return from;
    }

    switch (mode) {
      case 'startDate':
        if (startDate) {
          const startTime = new Date(startDate).getTime();
          const diffSeconds = (Date.now() - startTime) / 1000;
          return diffSeconds > 0 ? diffSeconds * growthRatio : 0;
        }
        return 0;
      case 'currentMonth':
        const now = new Date();
        const monthStart = new Date(
          now.getFullYear(),
          now.getMonth(),
          1
        ).getTime();
        const diffSecMonth = (Date.now() - monthStart) / 1000;
        return diffSecMonth > 0 ? diffSecMonth * growthRatio : 0;
      case 'static':
      default:
        return from;
    }
  }, [mode, startDate, growthRatio, from]);

  const [count, setCount] = useState(computedFrom);
  const currentValueRef = useRef(computedFrom);

  const startCycle = (startValue: number) => {
    if (!growthRatio) {
      return;
    }

    const incrementPerCycle = growthRatio * (duration + delay);
    const endValue = startValue + incrementPerCycle;

    onStart?.();

    let startTime: number | null = null;

    const animate = (timestamp: number) => {
      if (startTime === null) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      const easedProgress = easeOutCubic(progress);

      const newValue = startValue + (endValue - startValue) * easedProgress;
      setCount(newValue);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        currentValueRef.current = endValue;
        setCount(endValue);
        onEnd?.();

        timeoutRef.current = window.setTimeout(
          () => startCycle(endValue),
          delay * 1000
        );
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  };

  const animateToTarget = (startValue: number, endValue: number) => {
    onStart?.();

    let startTime: number | null = null;

    const animate = (timestamp: number) => {
      if (startTime === null) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      const easedProgress = easeOutCubic(progress);

      const newValue = startValue + (endValue - startValue) * easedProgress;
      setCount(newValue);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        currentValueRef.current = endValue;
        setCount(endValue);
        onEnd?.();
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (!isIntersecting) {
      return;
    }

    if (targetMode === 'staticTarget') {
      const endVal = targetNumber ?? currentValueRef.current;
      timeoutRef.current = window.setTimeout(
        () => animateToTarget(currentValueRef.current, endVal),
        3000
      );

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }

    timeoutRef.current = window.setTimeout(
      () => startCycle(currentValueRef.current),
      delay * 1000
    );

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // We don't need to re-run this effect if startCycle is signature changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isIntersecting,
    delay,
    duration,
    growthRatio,
    computedFrom,
    targetMode,
    targetNumber,
  ]);

  return {
    count,
    ref,
    isIntersecting,
  };
}
